// End-user shapes for Jarvis Patch Logs. Mirrors `PatchLogPublic` from the
// cross-repo contract (WIRE DETAILS) — the admin-only fields (noImageReason,
// sourcePr, aiDraftFlag, storageKey) are deliberately absent here.

export type PatchLogLocale = 'id' | 'en' | 'zh';

export type PatchLogCategory = 'fitur_baru' | 'perbaikan' | 'tampilan_berubah';

export type PatchLogImageType = 'single' | 'before' | 'after';

/** A localized string; every published log carries all three locales. */
export type LocalizedText = Record<PatchLogLocale, string>;

/** Localized point lists; the server guarantees equal lengths across locales. */
export type LocalizedPoints = Record<PatchLogLocale, string[]>;

export interface PatchLogImagePublic {
  id: number;
  position: number;
  /** Permanent HMAC-signed proxy link — opaque, use verbatim. */
  url: string;
  type: PatchLogImageType;
  altText: LocalizedText;
  width: number;
  height: number;
}

export interface PatchLogEntryPublic {
  id: number;
  position: number;
  category: PatchLogCategory;
  title: LocalizedText;
  /**
   * Where in Jarvis the change shows up, e.g. "Doc Pabean › Barang" ("›"
   * marks nesting, page › tab). Optional and never publish-blocking: absent
   * on payloads written before the field existed, and an empty string is valid
   * in any locale for cross-cutting changes with no single home. Read it with
   * `pickLocalized` and render nothing when the result is empty.
   */
  area?: LocalizedText;
  points: LocalizedPoints;
  images: PatchLogImagePublic[];
}

export interface PatchLogPublic {
  id: number;
  /** Free text, e.g. "Jarvis 2.14". */
  versionLabel: string;
  /** "YYYY-MM-DD". */
  releaseDate: string;
  publishedAt: string | null;
  entries: PatchLogEntryPublic[];
}

/** GET /api/v1/patch-logs/unread -> data */
export interface UnreadPatchLogsResponse {
  /** Newest first. Logs deferred 3+ times are excluded here and counted below. */
  logs: PatchLogPublic[];
  /** How many of the unread logs are past the 3-deferral cap, so no longer block. */
  badgeOnlyCount: number;
  /**
   * Everything still unconfirmed, `badgeOnlyCount` included — what the badge
   * shows. It deliberately does NOT drop to zero once the modal stops
   * blocking: three "remind me later" clicks are not the same as having read
   * the release notes.
   */
  unreadCount: number;
}

/** POST /api/v1/patch-logs/acknowledge body */
export interface AcknowledgePatchLogsPayload {
  patchLogIds: number[];
  localeRead: PatchLogLocale;
  scrolledToBottom: boolean;
}

/** POST /api/v1/patch-logs/acknowledge -> data */
export interface AcknowledgePatchLogsResponse {
  acknowledged: number[];
}

/** POST /api/v1/patch-logs/defer body */
export interface DeferPatchLogsPayload {
  patchLogIds: number[];
}

/** POST /api/v1/patch-logs/defer -> data */
export interface DeferPatchLogsResponse {
  deferrals: Array<{ patchLogId: number; count: number; badgeOnly: boolean }>;
}

export const PATCH_LOG_LOCALES: readonly PatchLogLocale[] = ['id', 'en', 'zh'];

export const isPatchLogLocale = (value: string | null | undefined): value is PatchLogLocale =>
  value === 'id' || value === 'en' || value === 'zh';

/**
 * Reads a localized field in the requested locale, falling back to Indonesian
 * and then to the first non-empty value. Published logs pass a locale gate, so
 * the fallback only matters for previews of unfinished drafts.
 */
export function pickLocalized<T extends string | string[]>(
  record: Partial<Record<PatchLogLocale, T>> | null | undefined,
  locale: PatchLogLocale,
  empty: T
): T {
  if (!record) {
    return empty;
  }
  const preferred = record[locale];
  if (preferred !== undefined && preferred.length > 0) {
    return preferred;
  }
  const fallback = record.id;
  if (fallback !== undefined && fallback.length > 0) {
    return fallback;
  }
  for (const code of PATCH_LOG_LOCALES) {
    const candidate = record[code];
    if (candidate !== undefined && candidate.length > 0) {
      return candidate;
    }
  }
  return empty;
}
